import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Plus, Edit2, Trash2, Star, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { getBackendUrl } from "@/utils/apiBase";

const API = getBackendUrl();

export const ReviewsManager = () => {
  const [reviews, setReviews] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [formData, setFormData] = useState({
    client_name: "",
    rating: 5,
    review_text: "",
    is_active: true,
  });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API}/api/reviews`);
      const data = await response.json();
      setReviews(data);
    } catch (error) {
      console.error("Erreur lors du chargement des avis:", error);
      toast.error("Erreur lors du chargement des avis");
    }
  };

  const handleOpenDialog = (review = null) => {
    if (review) {
      setEditingReview(review);
      setFormData({
        client_name: review.client_name,
        rating: review.rating,
        review_text: review.review_text,
        is_active: review.is_active,
      });
    } else {
      setEditingReview(null);
      setFormData({
        client_name: "",
        rating: 5,
        review_text: "",
        is_active: true,
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.rating < 1 || formData.rating > 5) {
      toast.error("Le rating doit être entre 1 et 5");
      return;
    }

    try {
      const url = editingReview
        ? `${API}/api/reviews/${editingReview.id}`
        : `${API}/api/reviews`;
      
      const method = editingReview ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          rating: parseInt(formData.rating),
        }),
      });

      if (response.ok) {
        toast.success(editingReview ? "Avis modifié" : "Avis créé");
        setIsDialogOpen(false);
        fetchReviews();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm("Supprimer cet avis ?")) return;

    try {
      const response = await fetch(`${API}/api/reviews/${reviewId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Avis supprimé");
        fetchReviews();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 font-['Playfair_Display']">
            <MessageSquare className="h-6 w-6 sm:h-8 sm:w-8 text-[#b8977e]" />
            Gestion des Avis Clients
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Gérez les témoignages affichés sur votre site
          </p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#b8977e] hover:bg-[#a68770] text-white w-full sm:w-auto rounded-lg"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nouvel Avis
        </Button>
      </div>

      {/* Reviews List */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {reviews.map((review) => (
          <Card key={review.id} className={`${review.is_active ? 'border-[#b8977e] bg-[#fef9f5]' : 'border-[#f0e6d3]'} hover:shadow-lg transition-shadow rounded-xl`}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start gap-2">
                <CardTitle className="text-base sm:text-lg flex items-center gap-2 flex-wrap font-['Playfair_Display']">
                  {review.client_name}
                  {review.is_active ? (
                    <Badge className="bg-[#b8977e] text-white text-xs">Actif</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Inactif</Badge>
                  )}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                {renderStars(review.rating)}
                <span className="text-sm font-semibold text-gray-700">{review.rating}/5</span>
              </div>

              <p className="text-xs sm:text-sm text-gray-600 line-clamp-3">
                "{review.review_text}"
              </p>

              <div className="text-xs text-gray-400">
                {new Date(review.created_at).toLocaleDateString('fr-FR')}
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => handleOpenDialog(review)}
                  size="sm"
                  variant="outline"
                  className="flex-1 text-xs sm:text-sm"
                >
                  <Edit2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  Éditer
                </Button>
                <Button
                  onClick={() => handleDelete(review.id)}
                  size="sm"
                  variant="outline"
                  className="flex-1 text-red-600 hover:bg-red-50 text-xs sm:text-sm"
                >
                  <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  Supprimer
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {reviews.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <MessageSquare className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-sm sm:text-base text-gray-500">Aucun avis créé</p>
              <Button
                onClick={() => handleOpenDialog()}
                className="mt-4 bg-rose-500 hover:bg-rose-600"
              >
                <Plus className="mr-2 h-4 w-4" />
                Créer le premier avis
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingReview ? "Modifier l'Avis" : "Nouvel Avis"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="client_name">Nom du client *</Label>
              <Input
                id="client_name"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                placeholder="ex: Marie Dupont"
                required
              />
            </div>

            <div>
              <Label htmlFor="rating">Note (1-5) *</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="rating"
                  type="number"
                  min="1"
                  max="5"
                  value={formData.rating}
                  onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                  className="w-20"
                  required
                />
                {renderStars(parseInt(formData.rating) || 0)}
              </div>
            </div>

            <div>
              <Label htmlFor="review_text">Témoignage *</Label>
              <Textarea
                id="review_text"
                value={formData.review_text}
                onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                placeholder="ex: Excellent service, très professionnel ! Notre mariage était parfait grâce au photobooth..."
                rows={5}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.review_text.length} caractères
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                {formData.is_active ? (
                  <ToggleRight className="h-5 w-5 text-green-600" />
                ) : (
                  <ToggleLeft className="h-5 w-5 text-gray-400" />
                )}
                <Label htmlFor="is_active" className="cursor-pointer">
                  Afficher sur le site
                </Label>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Annuler
              </Button>
              <Button type="submit" className="bg-rose-500 hover:bg-rose-600">
                {editingReview ? "Modifier" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
